/* Presentation rules for anomaly findings. Pure — no React, no fetch.
 *
 * **The four verdicts must stay four, and that is what this file is for.** `core.ai.anomaly` has two
 * verdicts that exist precisely because collapsing them is the failure mode: `INSUFFICIENT_HISTORY`
 * means the series is too short to say anything — a cold start, not a clean bill of health — and
 * `UNDETERMINED` means the maths did not apply, which happens to a perfectly flat series whose MAD is
 * zero, where every difference is infinitely many deviations. A screen that rendered either as
 * "normal" would report a table nobody has measured as healthy, and a table loading exactly 1,000 rows
 * a day as broken. Neither is a display bug; both are the platform lying about a customer's data.
 */

import type { AlertTone } from '@/shared/ui/Alert';
import type { BadgeTone } from '@/shared/ui/StatusBadge';

import type {
  DetectorInfo,
  Explanation,
  Finding,
  FindingDetail,
  Priority,
  SignalKind,
  Verdict,
} from '../api/anomaly';

/** Verdict → tone, four distinct answers.
 *
 * `insufficient_history` is `neutral` and `undetermined` is `unknown` — the hue this design system
 * reserves for "we could not tell", used nowhere on a red-amber-green scale. Deliberately *not* both
 * neutral: a cold start resolves itself as observations accumulate, while an undetermined verdict is a
 * property of the series that will persist, and an operator's next action differs.
 */
const VERDICT_TONES = {
  anomalous: 'danger',
  normal: 'ok',
  undetermined: 'unknown',
  insufficient_history: 'neutral',
} as const satisfies Record<Verdict, BadgeTone>;

export function verdictTone(verdict: Verdict): BadgeTone {
  /* **A total map with `satisfies`, not a `switch` with a `default:`.** The default clause was the hole
     in this file's own stated purpose: a fifth `Verdict` added to the contract would have rendered as
     `neutral` — the tone reserved for a cold start — and `tsc` would have said nothing. A map checked
     against `Record<Verdict, BadgeTone>` makes the omission a compile error, which is what a file whose
     first paragraph is "the four verdicts must stay four" should have had. */
  return VERDICT_TONES[verdict];
}

/** The i18n key naming a verdict in words, never the wire value.
 *
 * `insufficient_history` on screen makes a steward guess; "not enough history yet" states the fact and
 * implies the remedy, which is to wait.
 */
export function verdictKey(verdict: Verdict): string {
  return `anomaly.verdict.${verdict}`;
}

/** The i18n key for a signal, so `quality_score` never appears as a wire value. */
export function signalKey(signal: SignalKind): string {
  return `anomaly.signal.${signal}`;
}

/** Priority → tone, five distinct answers — deliberately *not* the same map as `verdictTone`.
 *
 * The verdict badge already reads `danger` for every `anomalous` row, critical or not; this
 * column exists to say which of those needs looking at *first* on a table with more than one,
 * so `critical` and `anomalous` get different tones here even though they share a verdict.
 */
const PRIORITY_TONES = {
  critical: 'danger',
  anomalous: 'warn',
  worth_reviewing: 'unknown',
  normal: 'ok',
  not_yet_measured: 'neutral',
} as const satisfies Record<Priority, BadgeTone>;

export function priorityTone(priority: Priority): BadgeTone {
  /* A total map, `verdictTone`'s own reasoning: a sixth `Priority` added to the contract must
     fail to compile here, not render as `neutral` silently. */
  return PRIORITY_TONES[priority];
}

/** The i18n key naming a priority in words, never the wire value. */
export function priorityKey(priority: Priority): string {
  return `anomaly.priority.${priority}`;
}

/** Findings ordered by priority — most urgent first — with a stable sort preserving whatever
 * order they arrived in for two findings that share a priority.
 *
 * **The ranking itself is not recomputed here.** `priority_rank` is the platform's own ordering,
 * sent on every finding for exactly this reason — a client re-deriving it from `verdict` and
 * `critical` would be a second answer to "which finding matters more" that could silently drift
 * from the server's, the two-vocabularies shape this codebase keeps a name for.
 */
export function sortedByPriority<T extends Finding>(
  findings: readonly T[],
): readonly T[] {
  return [...findings].sort((left, right) => left.priority_rank - right.priority_rank);
}

/** The i18n key naming why an explanation is or is not a model's words.
 *
 * **Four statuses, and two of them are the distinction §9 records this module collapsing once.**
 * `template_only` means no model was routed — a configuration fact about the deployment.
 * `unavailable` means one was routed and could not be reached — an outage. They arrived as the same
 * exception and were reported as the same thing, so every unconfigured tenant read as an outage.
 * `rejected` means the model answered and the validator refused it, which is the platform working.
 */
export function explanationKey(explanation: Explanation): string {
  return `anomaly.explanation.${explanation.status}`;
}

/** Whether an explanation's text was written by a model rather than by the platform.
 *
 * Read from `source`, never inferred from `status`: the two are separate fields because a *rejected*
 * model answer leaves the host's template in `text`, so a screen that inferred authorship from status
 * would attribute the platform's own words to a model whose answer was thrown away.
 */
export function isModelAuthored(explanation: Explanation): boolean {
  return explanation.source === 'model';
}

/** A finding's numbers as a short, readable phrase, or `null` when there are none to show.
 *
 * `null` rather than a placeholder for the cold-start and undetermined cases: those findings have no
 * `observed`/`baseline` pair, and rendering "0 vs 0" would invent a measurement. The whole point of
 * median-and-MAD over mean-and-standard-deviation is that the baseline is not dragged by the outlier
 * it is meant to flag, so the two figures are what makes a rationale checkable on paper — but only
 * when they exist.
 */
export function figuresOf(finding: Finding): string | null {
  const { observed, baseline } = finding;
  if (observed == null || baseline == null) return null;
  return `${formatNumber(observed)} vs ${formatNumber(baseline)}`;
}

/** How many deviations from the baseline, to one place, or `null` when undefined.
 *
 * A MAD of zero makes this infinite, which is exactly the `undetermined` case — so a non-finite value
 * is reported as absent rather than as `Infinity`, which would render as a number a steward would try
 * to interpret.
 */
export function deviationOf(finding: Finding): string | null {
  const { deviation } = finding;
  if (deviation == null || !Number.isFinite(deviation)) return null;
  return `${deviation.toFixed(1)}×`;
}

/** Compact number formatting: thousands separated, decimals only when they carry information. */
function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return Number.isInteger(value)
    ? value.toLocaleString()
    : value.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

/** Findings whose verdict is `anomalous`, newest first as the API returned them.
 *
 * Used for a count rather than a filter on the table: the table shows everything, because hiding the
 * cold-start rows is what makes an unmeasured estate look healthy — which is the trap this module's
 * brief names. The count is there so a steward knows how many rows deserve attention without the
 * screen deciding for them.
 */
export function anomalousCount(findings: readonly Finding[]): number {
  return findings.filter((finding) => finding.verdict === 'anomalous').length;
}

/** Verdict counts in a fixed order, so the summary does not reshuffle between evaluations.
 *
 * Every verdict is present even at zero, because **the zeros are the information**: "0 anomalous, 3
 * insufficient history" is the cold-start answer the outcome's `verdict_counts` exists to give, and
 * omitting empty buckets would make it indistinguishable from "3 normal".
 */
export function orderedVerdicts(
  counts: Readonly<Record<string, number>>,
): readonly { readonly verdict: Verdict; readonly count: number }[] {
  const order: readonly Verdict[] = [
    'anomalous',
    'normal',
    'undetermined',
    'insufficient_history',
  ];
  return order.map((verdict) => ({ verdict, count: counts[verdict] ?? 0 }));
}

/** Which sentence a missing dataset name gets. **Three states, because there are three causes.**
 *
 * A screen that holds a dataset id and cannot name it is about to make a claim, and the claim has to
 * match the evidence. The catalogue read failed: that is about the request, and it explains every row
 * at once. The name walk stopped at its bound: that is about us, and it explains this row without
 * asserting anything about the estate. Only when the catalogue was read to the end is "this dataset is
 * not in the listing" something the screen has grounds to say.
 *
 * `useDatasetNames` used to fetch one capped page, so an unresolved id had exactly one possible
 * reading and the cell asserted it — which is why the same bare-UUID defect has been recorded four
 * times, and why `DatasetNames.truncated` exists rather than being inferred from an empty map.
 *
 * Ordered most-specific first, and returning a key rather than a sentence so the wording stays in the
 * catalogue where it can be reviewed and translated in one place.
 */
export function nameGapKey(readable: boolean, truncated: boolean): string {
  if (!readable) return 'anomaly.catalogueUnreadable';
  if (truncated) return 'anomaly.catalogueTruncated';
  return 'anomaly.unnamedDataset';
}

/* ------------------------------------------------------------------ list filter & summary */

/** The verdict filter's options — `all` plus the four verdicts, in priority order so the segments
 * read most-severe-first, matching the table's own sort. */
export const VERDICT_FILTERS = [
  'all',
  'anomalous',
  'normal',
  'undetermined',
  'insufficient_history',
] as const;

export type VerdictFilter = (typeof VERDICT_FILTERS)[number];

/** Whether a finding is shown under the current verdict filter. `all` shows everything. */
export function matchesVerdict(finding: Finding, filter: VerdictFilter): boolean {
  return filter === 'all' || finding.verdict === filter;
}

/** Per-verdict counts, every verdict present even at zero — the zeros are the cold-start answer. */
export function summaryCounts(
  findings: readonly Finding[],
): readonly { readonly verdict: Verdict; readonly count: number }[] {
  return orderedVerdicts(
    findings.reduce<Record<string, number>>((counts, finding) => {
      counts[finding.verdict] = (counts[finding.verdict] ?? 0) + 1;
      return counts;
    }, {}),
  );
}

/** The KPI card's accent for a verdict — a class suffix, total over `Verdict` so a fifth member
 * fails to compile rather than rendering with the wrong colour. Only `anomalous` and `normal`
 * carry a hue; the two "we could not judge" verdicts are deliberately neutral. */
const KPI_TONES = {
  anomalous: 'danger',
  normal: 'ok',
  undetermined: 'neutral',
  insufficient_history: 'neutral',
} as const satisfies Record<Verdict, 'danger' | 'ok' | 'neutral'>;

export function kpiTone(verdict: Verdict): 'danger' | 'ok' | 'neutral' {
  return KPI_TONES[verdict];
}

/** The i18n key for a verdict's one-line KPI sub-label ("Outside the expected band"). */
export function summaryKey(verdict: Verdict): string {
  return `anomaly.summary.${verdict}`;
}

/* ------------------------------------------------------------------ deviation phrasing */

/** The compact deviation phrase the mockups use — `"−62% · 4.8σ"` — or `null` when a finding has
 * no observed/baseline pair (cold start) or no computable deviation (undetermined).
 *
 * The percentage is the change from the baseline; the sigma is the modified z-score. Both are the
 * finding's own numbers — nothing is re-derived, so this cannot drift from the verdict.
 */
export function deviationSummary(finding: Finding): string | null {
  const { observed, baseline, deviation } = finding;
  const parts: string[] = [];
  if (observed != null && baseline != null && baseline !== 0) {
    const pct = ((observed - baseline) / baseline) * 100;
    const rounded = Math.round(pct);
    const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : '';
    parts.push(`${sign}${String(Math.abs(rounded))}%`);
  }
  if (deviation != null && Number.isFinite(deviation)) {
    parts.push(`${deviation.toFixed(1)}σ`);
  }
  return parts.length === 0 ? null : parts.join(' · ');
}

/** The deviation column's cell text — always a string, never a bare dash.
 *
 * `undetermined` says `"σ = 0"` because that *is* what happened (a flat series, MAD zero); every
 * other verdict with no figures says `"Not computed"` — a cold start has nothing to compute against.
 */
export function deviationCell(finding: Finding): string {
  const summary = deviationSummary(finding);
  if (summary !== null) return summary;
  return finding.verdict === 'undetermined' ? 'σ = 0' : 'Not computed';
}

/* ------------------------------------------------------------------ explanation phrasing */

/** The first sentence of an explanation, for the list cell where space is tight.
 *
 * A host template is one sentence already; a model's can be up to three. The full text is on the
 * detail screen — the list wants the headline. Splits on `". "` so a decimal like `4.8` inside a
 * sentence is not treated as a boundary, and keeps the trailing period.
 */
export function shortExplanation(text: string): string {
  const trimmed = text.trim();
  const boundary = trimmed.search(/\.\s/);
  if (boundary === -1) return trimmed;
  return trimmed.slice(0, boundary + 1);
}

/* ------------------------------------------------------------------ detail screen */

/** The banner tone for a verdict on the detail screen. Total `Record`, `verdictTone`'s reasoning:
 * a fifth verdict must fail to compile here rather than render as a healthy green. */
const BANNER_TONES = {
  anomalous: 'error',
  normal: 'ok',
  undetermined: 'notice',
  insufficient_history: 'notice',
} as const satisfies Record<Verdict, AlertTone>;

export function bannerTone(verdict: Verdict): AlertTone {
  return BANNER_TONES[verdict];
}

/** What the detail screen's primary button does, by verdict.
 *
 * An anomalous finding can open an incident; every other verdict is silence, so the useful thing a
 * reader can do next is look at the rules that run on this dataset — hence "View executions".
 */
export function primaryAction(verdict: Verdict): 'raise-incident' | 'view-executions' {
  return verdict === 'anomalous' ? 'raise-incident' : 'view-executions';
}

/** How the detector's window reads in words. Prefers the server's phrasing (`detector.window`),
 * which is derived from the same bounds the verdict used. */
export function windowLabel(detector: DetectorInfo): string {
  return detector.window;
}

/** The sensitivity line — `"3.5σ"`, or `"2.0σ (overridden)"` when a steward has set one. */
export function sensitivityLabel(detector: DetectorInfo): {
  readonly value: string;
  readonly overridden: boolean;
} {
  return {
    value: `${detector.threshold.toFixed(1)}σ`,
    overridden: detector.threshold_override != null,
  };
}

/** The chart footer's centre clause — `"Baseline mean 69 · latest 24 (−62% · 4.8σ)"`. */
export function baselineFooter(detail: FindingDetail): string {
  const clauses: string[] = [];
  if (detail.baseline != null) clauses.push(`baseline ${formatNumber(detail.baseline)}`);
  if (detail.observed != null) clauses.push(`latest ${formatNumber(detail.observed)}`);
  const summary = deviationSummary(detail);
  const base = clauses.join(' · ');
  return summary === null ? base : `${base} (${summary})`;
}
